import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import API_CONFIG from '../config/api';
import useNetworkHierarchy from '../hooks/useNetworkHierarchy';
import DateRangePicker from '../components/DateRangePicker';
import {
  ClipboardDocumentListIcon,
  ChartBarIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  CurrencyEuroIcon,
  FunnelIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import Select from 'react-select';

// Import Chart.js
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineController,
  BarController
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';

// Registrazione componenti Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineController,
  BarController
);

// Funzione di utilità per raggruppare e ordinare i nodi per tipo e livello
const tipiNodi = [
  { tipo: 'agenzia_primaria', etichetta: 'Agenzie Primarie' },
  { tipo: 'team_leader', etichetta: 'Team Leader' },
  { tipo: 'agente', etichetta: 'Agenti' },
  { tipo: 'segnalatore', etichetta: 'Segnalatori' },
  { tipo: 'sito_vetrina', etichetta: 'Siti Vetrina' }
];

function raggruppaNodiPerTipo(nodi) {
  const gruppi = {};
  tipiNodi.forEach(t => gruppi[t.tipo] = []);
  nodi.forEach(n => {
    if (gruppi[n.tipo]) gruppi[n.tipo].push(n);
  });
  // Ordina ogni gruppo per livello e nome
  Object.keys(gruppi).forEach(tipo => {
    gruppi[tipo].sort((a, b) => (a.livello - b.livello) || a.nome.localeCompare(b.nome));
  });
  return gruppi;
}

// Funzione per costruire la gerarchia dei nodi sotto ogni agenzia primaria
function costruisciAlberoNodi(nodi, parentId = null, livello = 0) {
  return nodi
    .filter(n => n.parentId === parentId)
    .sort((a, b) => (a.tipo.localeCompare(b.tipo)) || a.nome.localeCompare(b.nome))
    .flatMap(n => [
      { ...n, livello },
      ...costruisciAlberoNodi(nodi, n.id, livello + 1)
    ]);
}

function raggruppaNodiPerAgenziaPrimaria(nodi, isAdmin, userNode, subordinateNodes) {
  // Filtra i nodi in base alla gerarchia dell'utente
  let nodiFiltrati = nodi;
  if (!isAdmin && userNode) {
    const nodiVisibili = [userNode.id, ...subordinateNodes.map(n => n.id)];
    nodiFiltrati = nodi.filter(nodo => nodiVisibili.includes(nodo.id));
  }
  
  const agenzie = nodiFiltrati.filter(n => n.tipo === 'agenzia_primaria');
  return agenzie.map(agenzia => {
    // Costruisci l'albero dei figli a partire dall'agenzia primaria
    const figli = costruisciAlberoNodi(nodiFiltrati, agenzia.id, 1);
    return {
      label: agenzia.nome + (agenzia.utente ? ` (${agenzia.utente.nome} ${agenzia.utente.cognome})` : ''),
      options: [
        {
          value: agenzia.id,
          label: agenzia.nome + (agenzia.utente ? ` (${agenzia.utente.nome} ${agenzia.utente.cognome})` : ''),
          tipo: agenzia.tipo,
          livello: 0,
          nodo: agenzia
        },
        ...figli.map(nodo => ({
          value: nodo.id,
          label: `${'— '.repeat(nodo.livello)}${nodo.nome}${nodo.utente ? ` (${nodo.utente.nome} ${nodo.utente.cognome})` : ''}`,
          tipo: nodo.tipo,
          livello: nodo.livello,
          nodo
        }))
      ]
    };
  });
}

const StatistichePratichePage = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    dataInizio: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], // Inizio anno
    dataFine: new Date().toISOString().split('T')[0] // Oggi
  });
  
  // Stati per i dati statistici
  const [statistichePratiche, setStatistichePratiche] = useState({
    riepilogoGenerale: {
      totalePratiche: 0,
      praticheCompletate: 0,
      praticheInCorso: 0,
      praticheEsitoNegativo: 0,
      tassoConversione: 0,
      tempoMedioCompleamento: 0
    },
    pratichePerStato: [],
    pratichePerMese: [],
    performanceAgenzie: [],
    tempiMediPerStato: [],
    topClientType: []
  });

  const [filtri, setFiltri] = useState({
    agenzia: '',
    stato: '',
    tipoCliente: ''
  });

  const [filtriVisibili, setFiltriVisibili] = useState(false);
  const [nodiDisponibili, setNodiDisponibili] = useState([]);

  // Recupera utente corrente da localStorage
  let user = null;
  try {
    user = JSON.parse(localStorage.getItem('user'));
  } catch (e) {
    user = null;
  }
  const ruoloUtente = (user?.role || user?.ruolo || '').toLowerCase();
  const isAdmin = ruoloUtente === 'admin';
  const isAgenzia = ruoloUtente === 'agenzia';
  const isSubagente = ruoloUtente === 'subagente' || ruoloUtente === 'agente' || ruoloUtente === 'agente_commerciale';

  // Hook per gestire la gerarchia della rete
  const { userNode, subordinateNodes, allNodes, canViewNode } = useNetworkHierarchy(user);

  // Imposta filtri di default basati sulla gerarchia
  useEffect(() => {
    if (!isAdmin && userNode) {
      // Per utenti non admin, imposta automaticamente il filtro sul loro nodo
      setFiltri(prev => ({
        ...prev,
        agenzia: userNode.id.toString()
      }));
    }
  }, [isAdmin, userNode]);

  // Carica lista nodi per il filtro (filtrati per gerarchia)
  const caricaNodiRete = async () => {
    try {
      const response = await axios.get(`${API_CONFIG.baseURL}/api/network/rete-corrente`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.data && response.data.data && response.data.data.nodi) {
        let nodiAttivi = response.data.data.nodi.filter(nodo => nodo.attivo);
        
        // Filtra i nodi in base alla gerarchia dell'utente
        if (!isAdmin && userNode) {
          // L'utente può vedere solo il suo nodo e quelli subordinati
          const nodiVisibili = [userNode.id, ...subordinateNodes.map(n => n.id)];
          nodiAttivi = nodiAttivi.filter(nodo => nodiVisibili.includes(nodo.id));
        }
        
        setNodiDisponibili(nodiAttivi);
      } else {
        setNodiDisponibili([]);
      }
    } catch (error) {
      setNodiDisponibili([]);
    }
  };

  // Carica le statistiche pratiche
  const caricaStatistichePratiche = async () => {
    try {
      setLoading(true);
      
      const response = await axios.get(`${API_CONFIG.baseURL}/api/statistiche/pratiche`, {
        params: {
          dataInizio: dateRange.dataInizio,
          dataFine: dateRange.dataFine,
          ...filtri
        },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data && response.data.success) {
        let statistiche = response.data.statistiche;
        
        // Filtra i dati delle statistiche in base alla gerarchia dell'utente
        if (!isAdmin && userNode && subordinateNodes.length >= 0) {
          const nodiVisibili = [userNode.id, ...subordinateNodes.map(n => n.id)];
          
          // Filtra le performance per agenzia - mostra solo agenzie visibili
          if (statistiche.performanceAgenzie) {
            statistiche.performanceAgenzie = statistiche.performanceAgenzie.filter(agenzia => {
              // Il backend dovrebbe già filtrare, ma aggiungiamo controllo di sicurezza
              // Poiché il backend filtra per idAgenziaPrimaria, dovrebbe essere già corretto
              return true; // Manteniamo per sicurezza
            });
          }
        }
        
        setStatistichePratiche(statistiche);
      } else {
        throw new Error('Formato dati non valido');
      }
      
    } catch (error) {
      console.error('Errore nel caricamento statistiche pratiche:', error);
      toast.error('Errore nel caricamento delle statistiche pratiche');
      
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (subordinateNodes.length >= 0) { // Aspetta che i dati siano caricati
      caricaNodiRete();
    }
  }, [subordinateNodes, isAdmin, userNode]);

  useEffect(() => {
    caricaStatistichePratiche();
  }, [dateRange, filtri]);

  const formatPercentage = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const getStatoIcon = (stato) => {
    switch (stato) {
      case 'pratica_avviata':
        return <ClockIcon className="h-4 w-4 text-blue-500" />;
      case 'contratto_attivo':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'pratica_chiusa':
        return <XCircleIcon className="h-4 w-4 text-red-500" />;
      case 'esito_negativo':
        return <XCircleIcon className="h-4 w-4 text-rose-500" />;
      case 'condizionata':
        return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />;
      case 'richiesta_integrazione':
        return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />;
      default:
        return <DocumentTextIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatoLabel = (stato) => {
    const labels = {
      'pratica_avviata': 'Pratica Avviata',
      'attesa_preventivo': 'Attesa Preventivo',
      'valutazione_cliente': 'Valutazione Cliente',
      'attesa_documenti_cliente': 'Attesa Documenti',
      'richiesta_esito': 'Richiesta Esito',
      'pratica_in_valutazione': 'In Valutazione',
      'attesa_firma_contratto': 'Attesa Firma',
      'contratto_attivo': 'Contratto Attivo',
      'richiesta_integrazione': 'Richiesta Integrazione',
      'pratica_chiusa': 'Pratica Chiusa',
      'esito_negativo': 'Esito Negativo',
      'condizionata': 'Condizionata'
    };
    return labels[stato] || stato;
  };

  // Configurazione grafico pratiche mensili (Linea + Barre)
  const chartPraticheMensili = {
    labels: statistichePratiche.pratichePerMese.map(item => item.mese),
    datasets: [
      {
        type: 'bar',
        label: 'Pratiche Create',
        data: statistichePratiche.pratichePerMese.map(item => item.create),
        backgroundColor: 'rgba(59, 130, 246, 0.6)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1,
      },
      {
        type: 'bar',
        label: 'Pratiche Completate',
        data: statistichePratiche.pratichePerMese.map(item => item.completate),
        backgroundColor: 'rgba(16, 185, 129, 0.6)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 1,
      },
      {
        type: 'line',
        label: 'Tasso Conversione %',
        data: statistichePratiche.pratichePerMese.map(item => item.conversione),
        backgroundColor: 'rgba(245, 158, 11, 0.2)',
        borderColor: 'rgba(245, 158, 11, 1)',
        borderWidth: 3,
        tension: 0.4,
        yAxisID: 'y1',
      }
    ]
  };

  const chartPraticheOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Andamento Pratiche Mensile'
      },
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: {
          drawOnChartArea: false,
        },
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return value + '%';
          }
        }
      },
    },
  };

  // Configurazione grafico stati (Doughnut)
  const chartStati = {
    labels: statistichePratiche.pratichePerStato.map(item => getStatoLabel(item.stato)),
    datasets: [
      {
        data: statistichePratiche.pratichePerStato.map(item => item.count),
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',   // Blu
          'rgba(16, 185, 129, 0.8)',   // Verde
          'rgba(245, 158, 11, 0.8)',   // Arancione
          'rgba(239, 68, 68, 0.8)',    // Rosso
          'rgba(139, 92, 246, 0.8)',   // Viola
          'rgba(6, 182, 212, 0.8)',    // Ciano
          'rgba(236, 72, 153, 0.8)',   // Rosa
          'rgba(34, 197, 94, 0.8)',    // Verde chiaro
          'rgba(251, 191, 36, 0.8)',   // Giallo
          'rgba(147, 51, 234, 0.8)'    // Indaco
        ],
        borderColor: [
          'rgba(59, 130, 246, 1)',
          'rgba(16, 185, 129, 1)',
          'rgba(245, 158, 11, 1)',
          'rgba(239, 68, 68, 1)',
          'rgba(139, 92, 246, 1)',
          'rgba(6, 182, 212, 1)',
          'rgba(236, 72, 153, 1)',
          'rgba(34, 197, 94, 1)',
          'rgba(251, 191, 36, 1)',
          'rgba(147, 51, 234, 1)'
        ],
        borderWidth: 2,
      },
    ],
  };

  const doughnutStatiOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: true,
        text: 'Distribuzione Pratiche per Stato'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.raw / total) * 100).toFixed(1);
            return `${context.label}: ${context.raw} (${percentage}%)`;
          }
        }
      }
    },
  };

  // Configurazione grafico performance agenzie (Barre orizzontali)
  const chartAgenzie = {
    labels: statistichePratiche.performanceAgenzie.map(item => item.nomeAgenzia || 'N/A'),
    datasets: [
      {
        label: 'Pratiche Gestite',
        data: statistichePratiche.performanceAgenzie.map(item => item.praticheGestite),
        backgroundColor: 'rgba(59, 130, 246, 0.6)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1,
      },
      {
        label: 'Pratiche Completate',
        data: statistichePratiche.performanceAgenzie.map(item => item.completate),
        backgroundColor: 'rgba(16, 185, 129, 0.6)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 1,
      }
    ]
  };

  const chartAgenzieOptions = {
    indexAxis: 'y',
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Performance Agenzie'
      },
    },
    scales: {
      x: {
        beginAtZero: true,
      },
    },
  };

  const resetFiltri = () => {
    setFiltri({
      agenzia: '',
      stato: '',
      tipoCliente: ''
    });
  };

  const [graficoEspanso, setGraficoEspanso] = useState(null);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <ClipboardDocumentListIcon className="h-8 w-8 mr-3 text-blue-600" />
                Statistiche Pratiche
              </h1>
              <p className="text-gray-600 mt-2">
                {isAdmin 
                  ? 'Analisi dettagliata dei flussi di lavoro e performance operative di tutto il sistema'
                  : `Analisi delle tue pratiche ${userNode ? `(${userNode.nome})` : ''} e dei nodi subordinati`
                }
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Filtri avanzati toggle */}
              <button
                onClick={() => setFiltriVisibili(!filtriVisibili)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <FunnelIcon className="h-5 w-5" />
                <span>Filtri</span>
              </button>
              </div>
            </div>

          {/* Date range - Sezione migliorata */}
          <div className="mt-6 bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center mb-4">
              <CalendarIcon className="h-5 w-5 text-blue-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Periodo di Analisi</h3>
            </div>
            <DateRangePicker
              startDate={dateRange.dataInizio}
              endDate={dateRange.dataFine}
              onDatesChange={(dates) => setDateRange({ dataInizio: dates.startDate, dataFine: dates.endDate })}
            />
          </div>

          {/* Panel filtri avanzati */}
          {filtriVisibili && (
            <div className="mt-6 bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Filtri Avanzati</h3>
                <button
                  onClick={() => setFiltriVisibili(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Filtro Agenzia */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Agenzia
                  </label>
                  {/* Select avanzata nodi rete con ricerca e gruppi per agenzia primaria */}
                  <Select
                    classNamePrefix="select-nodo-rete"
                    options={raggruppaNodiPerAgenziaPrimaria(nodiDisponibili, isAdmin, userNode, subordinateNodes)}
                    value={raggruppaNodiPerAgenziaPrimaria(nodiDisponibili, isAdmin, userNode, subordinateNodes).flatMap(g => g.options).find(opt => opt.value === filtri.agenzia) || null}
                    onChange={opt => setFiltri(prev => ({ ...prev, agenzia: opt ? opt.value : '' }))}
                    placeholder="Seleziona nodo della rete..."
                    isClearable
                    isSearchable
                    formatOptionLabel={(option, { context }) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: option.livello * 12 }}>
                        {/* Badge/icone per tipo nodo */}
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#888' }}>
                          {option.tipo === 'agenzia_primaria' && '🏢'}
                          {option.tipo === 'team_leader' && '👔'}
                          {option.tipo === 'agente' && '🧑‍💼'}
                          {option.tipo === 'segnalatore' && '📣'}
                          {option.tipo === 'sito_vetrina' && '🌐'}
                        </span>
                        <span>{option.label}</span>
                        {context === 'menu' && (
                          <span style={{ marginLeft: 8, fontSize: 11, color: '#aaa' }}>{option.tipo.replace('_', ' ')}</span>
                        )}
                      </div>
                    )}
                  />
                </div>

                {/* Filtro Stato */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stato Pratica
                  </label>
                  <select
                    value={filtri.stato}
                    onChange={(e) => setFiltri(prev => ({ ...prev, stato: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Tutti gli stati</option>
                    <option value="pratica_avviata">Pratica Avviata</option>
                    <option value="attesa_preventivo">Attesa Preventivo</option>
                    <option value="valutazione_cliente">Valutazione Cliente</option>
                    <option value="attesa_documenti_cliente">Attesa Documenti</option>
                    <option value="richiesta_esito">Richiesta Esito</option>
                    <option value="pratica_in_valutazione">In Valutazione</option>
                    <option value="attesa_firma_contratto">Attesa Firma</option>
                    <option value="contratto_attivo">Contratto Attivo</option>
                    <option value="richiesta_integrazione">Richiesta Integrazione</option>
                    <option value="pratica_chiusa">Pratica Chiusa</option>
                  </select>
                </div>

                {/* Filtro Tipo Cliente */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo Cliente
                  </label>
                  <select
                    value={filtri.tipoCliente}
                    onChange={(e) => setFiltri(prev => ({ ...prev, tipoCliente: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Tutti i tipi</option>
                    <option value="Privato">Privato</option>
                    <option value="Azienda">Azienda</option>
                    <option value="Partita IVA">Partita IVA</option>
                  </select>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={resetFiltri}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Reset Filtri
                </button>
              </div>
            </div>
          )}
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Totale Pratiche</p>
                <p className="text-2xl font-bold text-gray-900">
                  {statistichePratiche.riepilogoGenerale.totalePratiche}
                </p>
              </div>
              <ClipboardDocumentListIcon className="h-8 w-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completate</p>
                <p className="text-2xl font-bold text-green-600">
                  {statistichePratiche.riepilogoGenerale.praticheCompletate}
                </p>
              </div>
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">In Corso</p>
                <p className="text-2xl font-bold text-orange-600">
                  {statistichePratiche.riepilogoGenerale.praticheInCorso}
                </p>
              </div>
              <ClockIcon className="h-8 w-8 text-orange-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Esito Negativo</p>
                <p className="text-2xl font-bold text-rose-600">
                  {statistichePratiche.riepilogoGenerale.praticheEsitoNegativo || 0}
                </p>
              </div>
              <XCircleIcon className="h-8 w-8 text-rose-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Tasso Conversione</p>
                <p className="text-2xl font-bold text-purple-600">
                  {formatPercentage(statistichePratiche.riepilogoGenerale.tassoConversione)}
                </p>
              </div>
              <ArrowTrendingUpIcon className="h-8 w-8 text-purple-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Tempo Medio</p>
                <p className="text-2xl font-bold text-indigo-600">
                  {statistichePratiche.riepilogoGenerale.tempoMedioCompleamento}g
                </p>
              </div>
              <ClockIcon className="h-8 w-8 text-indigo-500" />
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-3 mb-4">
          {/* Grafico Andamento Mensile */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-3 border border-gray-200 relative overflow-x-auto">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Andamento Pratiche Mensile</h3>
              <button
                className="text-xs text-blue-500 hover:underline focus:outline-none"
                onClick={() => setGraficoEspanso(graficoEspanso === 'mensile' ? null : 'mensile')}
              >
                {graficoEspanso === 'mensile' ? 'Comprimi' : 'Espandi'}
              </button>
            </div>
            <div className={
              `w-full ${graficoEspanso === 'mensile' ? 'h-[420px] sm:h-[520px]' : 'h-44 sm:h-64'} aspect-[2/1]`
            }>
              {statistichePratiche.pratichePerMese.length > 0 ? (
                <Bar data={chartPraticheMensili} options={{
                  ...chartPraticheOptions,
                  maintainAspectRatio: false,
                  plugins: {
                    ...chartPraticheOptions.plugins,
                    legend: {
                      position: 'bottom',
                      labels: {
                        font: { size: 11 },
                        padding: 10
                      }
                    }
                  }
                }} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  Nessun dato disponibile
                </div>
              )}
            </div>
          </div>

          {/* Grafico Stati */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-3 border border-gray-200 relative overflow-x-auto">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Distribuzione per Stato</h3>
              <button
                className="text-xs text-blue-500 hover:underline focus:outline-none"
                onClick={() => setGraficoEspanso(graficoEspanso === 'stati' ? null : 'stati')}
              >
                {graficoEspanso === 'stati' ? 'Comprimi' : 'Espandi'}
              </button>
            </div>
            <div className={
              `w-full ${graficoEspanso === 'stati' ? 'h-[420px] sm:h-[520px]' : 'h-44 sm:h-64'} aspect-[2/1]`
            }>
              {statistichePratiche.pratichePerStato.length > 0 ? (
                <Doughnut data={chartStati} options={{
                  ...doughnutStatiOptions,
                  maintainAspectRatio: false,
                  plugins: {
                    ...doughnutStatiOptions.plugins,
                    legend: {
                      position: 'bottom',
                      labels: {
                        font: { size: 11 },
                        padding: 10
                      }
                    }
                  }
                }} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  Nessun dato disponibile
                </div>
              )}
            </div>
          </div>

          {/* Performance Agenzie Chart compatta */}
          {(isAdmin || statistichePratiche.performanceAgenzie.length > 0) && (
            <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-200 relative overflow-x-auto flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">{isAdmin ? 'Performance per Agenzia' : 'Performance Rete'}</h3>
                <button
                  className="text-xs text-blue-500 hover:underline focus:outline-none"
                  onClick={() => setGraficoEspanso(graficoEspanso === 'agenzie' ? null : 'agenzie')}
                >
                  {graficoEspanso === 'agenzie' ? 'Comprimi' : 'Espandi'}
                </button>
              </div>
              <div className={
                `w-full ${graficoEspanso === 'agenzie' ? 'h-[340px] sm:h-[420px]' : 'h-32 sm:h-40'} aspect-[2/1]`
              }>
                {statistichePratiche.performanceAgenzie.length > 0 ? (
                  <Bar data={chartAgenzie} options={{
                    ...chartAgenzieOptions,
                    maintainAspectRatio: false,
                    plugins: {
                      ...chartAgenzieOptions.plugins,
                      legend: {
                        position: 'bottom',
                        labels: {
                          font: { size: 11 },
                          padding: 10
                        }
                      }
                    }
                  }} />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    Nessun dato disponibile
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Bottom Row - Tabelle */}
        <div className={`grid grid-cols-1 gap-6 ${(isAdmin || statistichePratiche.performanceAgenzie.length > 0) ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
          {/* Performance Dettagliata */}
          {(isAdmin || statistichePratiche.performanceAgenzie.length > 0) && (
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{isAdmin ? 'Performance Dettagliata' : 'Performance Rete'}</h3>
              <div className="space-y-4">
                {statistichePratiche.performanceAgenzie.length > 0 ? (
                  statistichePratiche.performanceAgenzie.map((agenzia, index) => (
                    <div key={index} className="border-b border-gray-100 pb-4 last:border-b-0">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-900">
                          {agenzia.nomeAgenzia || 'N/A'}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          (agenzia.crescita || 0) >= 0 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {formatPercentage(agenzia.crescita || 0)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-gray-600">Gestite: </span>
                          <strong>{agenzia.praticheGestite || 0}</strong>
                        </div>
                        <div>
                          <span className="text-gray-600">Completate: </span>
                          <strong className="text-green-600">{agenzia.completate || 0}</strong>
                        </div>
                        <div>
                          <span className="text-gray-600">Conversione: </span>
                          <strong>{formatPercentage(agenzia.tassoConversione || 0)}</strong>
                        </div>
                        <div>
                          <span className="text-gray-600">Tempo: </span>
                          <strong>{agenzia.tempoMedio || 0}g</strong>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 py-4">
                    Nessun dato disponibile
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tempi Medi per Stato */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Tempi Medi per Stato</h3>
            <div className="space-y-3">
              {statistichePratiche.tempiMediPerStato.length > 0 ? (
                statistichePratiche.tempiMediPerStato.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      {getStatoIcon(item.stato)}
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {getStatoLabel(item.stato)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {item.descrizione}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-gray-900">
                        {item.tempoMedio}g
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 py-4">
                  Nessun dato disponibile
                </div>
              )}
            </div>
          </div>

          {/* Tipologie Cliente */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance per Tipo Cliente</h3>
            <div className="space-y-4">
              {statistichePratiche.topClientType.length > 0 ? (
                statistichePratiche.topClientType.map((tipo, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <UserGroupIcon className="h-5 w-5 text-blue-500" />
                        <span className="font-medium text-gray-900">{tipo.tipo}</span>
                      </div>
                      <span className="text-sm font-bold text-blue-600">
                        {formatPercentage(tipo.percentuale)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-600">Totali: </span>
                        <strong>{tipo.pratiche}</strong>
                      </div>
                      <div>
                        <span className="text-gray-600">Completate: </span>
                        <strong className="text-green-600">{tipo.completate}</strong>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 py-4">
                  Nessun dato disponibile
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StatistichePratichePage; 